import { useCallback, useEffect, useRef, useState } from "react";
import { Upload, Trash2, Film, Download, FileText } from "lucide-react";
import { buildPacks, videoBriefAsset, VIDEO_BRIEF_DOCX } from "@/components/production/printKit";
import { usePrintJob } from "@/components/production/usePrintJob";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { mediaUrl } from "@/lib/api";
import { prod, errMsg, fmtDate, fmtMB } from "@/lib/prodApi";

const MAX = 500 * 1024 * 1024;
const OK_EXT = ["mp4", "mov", "webm"];

async function putChunk(uid, i, blob, onProgress) {
  for (let attempt = 0; ; attempt += 1) {
    try {
      return await prod.put(`/videos/uploads/${uid}/chunks/${i}`, blob, { headers: { "Content-Type": "application/octet-stream" }, onUploadProgress: onProgress });
    } catch (e) {
      if (attempt >= 2 || (e.response && e.response.status < 500)) throw e;
    }
  }
}

function SlotCard({ slot, reload }) {
  const input = useRef(null);
  const [pct, setPct] = useState(null);
  const [err, setErr] = useState(null);
  const id = slot.slot;

  const upload = async (file) => {
    setErr(null);
    const ext = (file.name.includes(".") ? file.name.split(".").pop() : "").toLowerCase();
    if (!OK_EXT.includes(ext)) return setErr("Only MP4, MOV or WebM video files can be uploaded. The existing video has been kept.");
    if (file.size > MAX) return setErr(`This file is ${fmtMB(file.size)}. The limit is 500 MB. The existing video has been kept.`);
    setPct(0);
    try {
      const { data } = await prod.post(`/videos/${id}/uploads`, { filename: file.name, size: file.size, content_type: file.type || "" });
      const cs = data.chunk_size;
      for (let i = 0; i < data.chunks; i += 1) {
        const blob = file.slice(i * cs, Math.min(file.size, (i + 1) * cs));
        await putChunk(data.upload_id, i, blob, (e) => setPct(Math.min(99, Math.round(((i * cs + e.loaded) / file.size) * 100))));
      }
      await prod.post(`/videos/uploads/${data.upload_id}/complete`);
      setPct(100);
      toast.success(`${slot.label}: video uploaded`);
      reload();
    } catch (e) {
      setErr(errMsg(e, "The upload failed. The existing video has been kept."));
    } finally {
      setTimeout(() => setPct(null), 900);
    }
    return null;
  };

  const [confirmRemove, setConfirmRemove] = useState(false);
  const remove = async () => {
    setConfirmRemove(false);
    try {
      await prod.delete(`/videos/${id}`);
      toast.success(`${slot.label}: video removed`);
    } catch (e) {
      toast.error(errMsg(e, "The video could not be removed."));
    }
    reload();
  };

  const ready = slot.status === "ready";
  return (
    <div className="panel" data-testid={`video-slot-${id}`}>
      <div className="flex items-center justify-between px-6 py-4 border-b border-[#b1b4b6]">
        <h3 className="text-2xl font-black">{slot.label}</h3>
        <strong className={`tag ${ready ? "tag-green" : "tag-grey"}`} data-testid={`video-slot-${id}-status`}>{ready ? "Video uploaded" : "Footage pending"}</strong>
      </div>
      <div className="p-6">
        {ready ? (
          <video className="w-full aspect-video bg-black" src={mediaUrl(slot.preview_url)} controls preload="metadata" data-testid={`video-slot-${id}-player`} />
        ) : (
          <div className="w-full aspect-video bg-[#0b0c0c] text-white/70 flex flex-col items-center justify-center scanlines"><Film size={38} /><span className="font-mono tracking-[0.3em] mt-3">FOOTAGE PENDING</span></div>
        )}
        {ready && <p className="text-[16px] mt-3" data-testid={`video-slot-${id}-file`}><strong>{slot.filename}</strong> · {fmtMB(slot.size)} · {fmtDate(slot.uploaded_at)}</p>}
        {id === "damning_cale" && <p className="text-[16px] font-bold mt-3 pl-3 border-l-4 border-[#ffdd00]" data-testid="video-slot-damning_cale-note">v2 chamber-interior video installed. The control-room angle goes on the USB only.</p>}
        <p className="text-[16px] text-[#505a5f] mt-1">{slot.kind === "arrest" ? "Plays after a correct charge (CASE CLOSED)." : "Plays once players unlock this suspect's hidden clue."}</p>
        {pct !== null && (
          <div className="mt-4" data-testid={`video-slot-${id}-progress`}>
            <div className="h-4 bg-[#e5e6e7]"><div className="h-4 bg-[#0033A1] transition-[width]" style={{ width: `${pct}%` }} /></div>
            <span className="text-[16px] font-bold">Uploading… {pct}%</span>
          </div>
        )}
        {err && <p className="form-error error-msg mt-4 text-[17px]" data-testid={`video-slot-${id}-error`}>{err}</p>}
        <div className="flex gap-3 mt-5">
          <input ref={input} type="file" className="hidden" accept="video/mp4,video/quicktime,video/webm,.mp4,.mov,.webm"
            onChange={(e) => { const f = e.target.files[0]; e.target.value = ""; if (f) upload(f); }} data-testid={`video-slot-${id}-input`} />
          <button className="btn-spore !py-2" disabled={pct !== null} onClick={() => input.current.click()} data-testid={`video-slot-${id}-upload`}>
            <Upload size={18} /> {ready ? "Replace video" : "Upload video"}
          </button>
          {ready && <button className="btn-ghost !py-2" disabled={pct !== null} onClick={() => setConfirmRemove(true)} data-testid={`video-slot-${id}-remove`}><Trash2 size={18} /> Remove</button>}
        </div>
        <p className="text-[15px] text-[#505a5f] mt-3">MP4, MOV or WebM, up to 500 MB.</p>
      </div>
      <AlertDialog open={confirmRemove} onOpenChange={setConfirmRemove}>
        <AlertDialogContent className="rounded-none border-2 border-[#0b0c0c] max-w-xl" data-testid={`video-slot-${id}-remove-dialog`}>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-3xl font-black">Remove the {slot.label} video?</AlertDialogTitle>
            <AlertDialogDescription className="text-lg text-[#0b0c0c]">Players will see "Footage pending" instead.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-3">
            <AlertDialogCancel className="btn-ghost h-auto" data-testid={`video-slot-${id}-remove-cancel`}>Cancel</AlertDialogCancel>
            <AlertDialogAction className="btn-spore btn-danger h-auto" onClick={remove} data-testid={`video-slot-${id}-remove-confirm`}>Remove video</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default function VideoSlots() {
  const [slots, setSlots] = useState(null);
  const [packs, setPacks] = useState(null);
  const { job, start, root } = usePrintJob();
  const reload = useCallback(() => prod.get("/videos").then((r) => setSlots(r.data)), []);
  useEffect(() => {
    reload();
    prod.get("/print/content").then((r) => setPacks(buildPacks(r.data)));
  }, [reload]);
  if (!slots) return <p className="text-lg py-8">Loading video slots…</p>;
  return (
    <div data-testid="videos-section">
      <div className="flex flex-wrap items-end gap-4 mb-8">
        <div className="flex-1 min-w-[420px]">
          <h2 className="text-4xl font-black">Videos</h2>
          <p className="text-lg text-[#505a5f] mt-2">Uploaded videos replace "Footage pending" in the game straight away.</p>
        </div>
        <button className="btn-spore" disabled={!packs || !!job} onClick={() => start("pdf", [videoBriefAsset(packs)], "CCTV video production brief", "CCTV_video_production_brief")} data-testid="download-video-brief-pdf">
          <Download size={18} /> {job ? "Producing PDF…" : "Download video brief (PDF)"}
        </button>
        <a className="btn-ghost" href={mediaUrl(VIDEO_BRIEF_DOCX)} download data-testid="download-video-brief-docx"><FileText size={18} /> Download video brief (Word)</a>
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">{slots.map((s) => <SlotCard key={s.slot} slot={s} reload={reload} />)}</div>
      {root}
    </div>
  );
}
